import { CalculationMethod, CalculationParameters, Coordinates, PrayerTimes, Prayer, Madhab, HighLatitudeRule } from 'adhan';
import { CalcSettings, LocationData } from '../types';
import { IshaFixedSettings, FajrFixedSettings } from '../hooks/useGlobalStorage';
import { HelsinkiTimes } from './times2026';

const getDateKey = (date: Date): string => {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  return `${day}${month}`;
};

const isHelsinkiArea = (city: string): boolean => {
  const c = city.toLowerCase();
  return c.includes('helsinki') || c.includes('espoo') || c.includes('vantaa');
};

const applyHelsinkiTimes = (pt: PrayerTimes, dateKey: string): void => {
  const ht = HelsinkiTimes[dateKey as keyof typeof HelsinkiTimes];
  if (!ht) return;
  const set = (target: Date, time: string) => {
    const [h, m] = time.split(':').map(Number);
    target.setHours(h, m, 0, 0);
  };
  set(pt.fajr, ht.fajr);
  set(pt.sunrise, ht.sunrise);
  set(pt.dhuhr, ht.zuhur);
  set(pt.asr, ht.asr);
  set(pt.maghrib, ht.maghrib);
  set(pt.isha, ht.isha);
};

const applyIshaSettings = (pt: PrayerTimes, settings?: IshaFixedSettings): void => {
  if (!settings || settings.mode === 'default') return;
  if (settings.mode === 'afterMaghrib' && settings.minutesAfterMaghrib) {
    const t = new Date(pt.maghrib);
    t.setMinutes(t.getMinutes() + settings.minutesAfterMaghrib);
    pt.isha = t;
  } else if (settings.mode === 'minutesFromIsha' && settings.minutesFromRealIsha != null) {
    const t = new Date(pt.isha);
    t.setMinutes(t.getMinutes() + settings.minutesFromRealIsha);
    pt.isha = t;
  } else if (settings.mode === 'fixedTime' && settings.fixedTime) {
    const [h, m] = settings.fixedTime.split(':').map(Number);
    const t = new Date(pt.isha);
    t.setHours(h, m, 0, 0);
    pt.isha = t;
  }
};

const applyFajrSettings = (pt: PrayerTimes, settings?: FajrFixedSettings): void => {
  if (!settings || settings.mode === 'default') return;
  if (settings.mode === 'beforeSunrise' && settings.minutesBeforeSunrise) {
    const t = new Date(pt.sunrise);
    t.setMinutes(t.getMinutes() - settings.minutesBeforeSunrise);
    pt.fajr = t;
  } else if (settings.mode === 'minutesFromFajr' && settings.minutesFromRealFajr != null) {
    const t = new Date(pt.fajr);
    t.setMinutes(t.getMinutes() + settings.minutesFromRealFajr);
    pt.fajr = t;
  } else if (settings.mode === 'fixedTime' && settings.fixedTime) {
    const [h, m] = settings.fixedTime.split(':').map(Number);
    const t = new Date(pt.fajr);
    t.setHours(h, m, 0, 0);
    pt.fajr = t;
  }
};

export const buildPrayerTimesForDate = (
  city: string,
  date: Date,
  location: LocationData,
  calcSettings: CalcSettings,
  ishaSettings?: IshaFixedSettings,
  fajrSettings?: FajrFixedSettings,
): PrayerTimes => {
  const coordinates = new Coordinates(location.latitude, location.longitude);
  const params = getCalculationParams(calcSettings);
  const pt = new PrayerTimes(coordinates, date, params);

  const effectiveCity = city || location.city || '';
  if (isHelsinkiArea(effectiveCity)) {
    applyHelsinkiTimes(pt, getDateKey(date));
  }

  applyIshaSettings(pt, ishaSettings);
  applyFajrSettings(pt, fajrSettings);

  return pt;
};

export const getCalculationParams = (settings: CalcSettings): CalculationParameters => {
  const methodMap: Record<string, () => CalculationParameters> = {
    MuslimWorldLeague: CalculationMethod.MuslimWorldLeague,
    Egyptian: CalculationMethod.Egyptian,
    Karachi: CalculationMethod.Karachi,
    UmmAlQura: CalculationMethod.UmmAlQura,
    Dubai: CalculationMethod.Dubai,
    MoonsightingCommittee: CalculationMethod.MoonsightingCommittee,
    NorthAmerica: CalculationMethod.NorthAmerica,
    Kuwait: CalculationMethod.Kuwait,
    Qatar: CalculationMethod.Qatar,
    Singapore: CalculationMethod.Singapore,
    Turkey: CalculationMethod.Turkey,
    Tehran: CalculationMethod.Tehran,
  };

  const params = (methodMap[settings.method] || CalculationMethod.MuslimWorldLeague)();

  params.madhab = settings.madhab === 'Hanafi' ? Madhab.Hanafi : Madhab.Shafi;

  const hlrMap: Record<string, string> = {
    MiddleOfTheNight: HighLatitudeRule.MiddleOfTheNight,
    SeventhOfTheNight: HighLatitudeRule.SeventhOfTheNight,
    TwilightAngle: HighLatitudeRule.TwilightAngle,
  };
  if (settings.highLatitudeRule && hlrMap[settings.highLatitudeRule]) {
    params.highLatitudeRule = hlrMap[settings.highLatitudeRule] as any;
  }

  return params;
};

export const listPrayerTimes = (pt: PrayerTimes) => [
  { name: 'Fajr', at: pt.fajr, id: 'fajr' },
  { name: 'Sunrise', at: pt.sunrise, id: 'sunrise' },
  { name: 'Dhuhr', at: pt.dhuhr, id: 'dhuhr' },
  { name: 'Asr', at: pt.asr, id: 'asr' },
  { name: 'Maghrib', at: pt.maghrib, id: 'maghrib' },
  { name: 'Isha', at: pt.isha, id: 'isha' },
];

export const prayerEnumToName = (p: any): string => {
  switch (p) {
    case Prayer.Fajr: return 'Fajr';
    case Prayer.Sunrise: return 'Sunrise';
    case Prayer.Dhuhr: return 'Dhuhr';
    case Prayer.Asr: return 'Asr';
    case Prayer.Maghrib: return 'Maghrib';
    case Prayer.Isha: return 'Isha';
    default: return 'Fajr';
  }
};

export const formatTime = (d: Date) => {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export const msToHhMm = (ms: number) => {
  const totalMin = Math.max(0, Math.floor(ms / 60000));
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}h ${m}m`;
};
